import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Download, ShieldCheck, AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const STATUS_COLORS = { active: C.green, cancelled: C.red, expired: C.slate };
const CLAIM_TYPES = ['property', 'motor', 'liability', 'theft', 'water_damage', 'fire', 'storm', 'glass', 'other'];

const fmt$ = (v) => v != null ? `$${Number(v).toLocaleString('en-AU')}` : '—';
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em] mb-3" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

function FnolForm({ token, onLodged }) {
  const [form, setForm] = useState({ claimant_name: '', claimant_email: '', claim_type: 'property', date_of_loss: '', description: '', estimated_quantum: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().slice(0, 10);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/portal/policy/${token}/fnol`, {
        ...form, estimated_quantum: Number(form.estimated_quantum) || 0, date_of_loss: form.date_of_loss || null,
      });
      onLodged(data);
    } catch (e) { setError(e?.response?.data?.detail || 'Could not lodge the claim — please try again'); }
    setBusy(false);
  };

  const inputCls = "w-full px-3 py-2 rounded-sm text-sm bg-white outline-none focus:border-[#1A4C7C]";
  const inputStyle = { border: '1px solid rgba(136,150,168,0.3)', color: C.navy };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4" data-testid="portal-fnol-form">
      <input className={inputCls} style={inputStyle} placeholder="Your name *" value={form.claimant_name} onChange={set('claimant_name')} data-testid="portal-fnol-name" />
      <input className={inputCls} style={inputStyle} placeholder="Email (for updates)" value={form.claimant_email} onChange={set('claimant_email')} data-testid="portal-fnol-email" />
      <select className={inputCls} style={inputStyle} value={form.claim_type} onChange={set('claim_type')} data-testid="portal-fnol-type">
        {CLAIM_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
      </select>
      <input className={inputCls} style={inputStyle} type="date" max={today} value={form.date_of_loss} onChange={set('date_of_loss')} data-testid="portal-fnol-dol" />
      <textarea className={`${inputCls} md:col-span-2`} style={inputStyle} rows={3} placeholder="What happened? (min 10 characters) *" value={form.description} onChange={set('description')} data-testid="portal-fnol-desc" />
      <input className={inputCls} style={inputStyle} type="number" min="0" placeholder="Estimated amount ($, optional)" value={form.estimated_quantum} onChange={set('estimated_quantum')} data-testid="portal-fnol-quantum" />
      <div className="flex items-center justify-end gap-3">
        {error && <span className="text-xs" style={{ color: C.red }} data-testid="portal-fnol-error">{error}</span>}
        <button onClick={submit} disabled={busy || form.claimant_name.trim().length < 2 || form.description.trim().length < 10}
          className="px-5 py-2 rounded-sm text-sm font-medium text-white disabled:opacity-40"
          style={{ background: C.blue }} data-testid="portal-fnol-submit">
          {busy ? 'Lodging…' : 'Lodge Claim'}
        </button>
      </div>
    </div>
  );
}

function RenewalOfferCard({ token, renewal, onAccepted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const accepted = renewal.status === 'accepted';
  const changePct = renewal.premium_change_pct || 0;

  const accept = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/portal/policy/${token}/renewal/${renewal.renewal_id}/accept`);
      onAccepted(data);
    } catch (e) { setError(e?.response?.data?.detail || 'Could not accept the renewal — please contact your broker'); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-sm p-6" style={{ border: `1px solid ${accepted ? 'rgba(29,158,117,0.35)' : 'rgba(0,180,216,0.45)'}`, borderTop: `3px solid ${accepted ? C.green : C.teal}` }} data-testid="portal-renewal-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4" style={{ color: accepted ? C.green : C.teal }} />
          <Overline>{accepted ? 'Renewal accepted' : 'Your renewal offer'}</Overline>
        </div>
        {!accepted && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${changePct > 0 ? C.amber : C.green}15`, color: changePct > 0 ? '#B27516' : C.green }} data-testid="portal-renewal-change">
            {changePct > 0 ? '+' : ''}{changePct}% vs last year
          </span>
        )}
      </div>

      {accepted ? (
        <div className="flex items-center gap-3 mt-1" data-testid="portal-renewal-accepted">
          <ShieldCheck className="w-6 h-6 shrink-0" style={{ color: C.green }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: C.green }}>
              You're covered for another year{renewal.new_policy_number ? <> — new policy <span className="font-mono">{renewal.new_policy_number}</span></> : ''}
            </p>
            <p className="text-xs mt-0.5" style={{ color: C.slate }}>Cover runs {fmtD(renewal.new_inception)} – {fmtD(renewal.new_expiry)}. Your documents will be issued shortly.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-4">
            {[['New period of cover', `${fmtD(renewal.new_inception)} – ${fmtD(renewal.new_expiry)}`],
              ['Renewal premium', fmt$(renewal.renewal_premium)],
              ['Last year', fmt$(renewal.current_premium)],
              ['Sum insured', fmt$(renewal.sum_insured)]].map(([l, v]) => (
              <div key={l}>
                <p className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{l}</p>
                <p className="text-sm font-medium mt-1" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{v}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3 mt-5">
            <p className="text-xs" style={{ color: C.slate }}>One click binds your renewal on the same terms — no paperwork, no phone calls.</p>
            <div className="flex items-center gap-3">
              {error && <span className="text-xs" style={{ color: C.red }} data-testid="portal-renewal-error">{error}</span>}
              <button onClick={accept} disabled={busy}
                className="px-5 py-2.5 rounded-sm text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: C.green }} data-testid="portal-renewal-accept">
                {busy ? 'Binding…' : `Accept & Renew — ${fmt$(renewal.renewal_premium)}`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function PolicyholderPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showFnol, setShowFnol] = useState(false);
  const [lodged, setLodged] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/portal/policy/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'This link is invalid or has expired'));
  }, [token]);

  const p = data?.policy;
  const statusColor = STATUS_COLORS[p?.status] || C.slate;

  return (
    <div className="min-h-screen" style={{ background: '#F5F7FA' }} data-testid="policyholder-portal">
      <div style={{ background: C.navy }} className="px-6 py-5">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="text-lg font-bold text-white">under<span style={{ color: C.teal }}>AI</span>ter</span>
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Georgia, serif' }}>
            {data ? data.mga_name : 'Your Policy'}
          </span>
        </div>
      </div>
      <div style={{ height: 3, background: C.teal }} />

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {error && (
          <div className="bg-white rounded-sm p-8 text-center" style={{ border: '1px solid rgba(226,75,74,0.3)' }} data-testid="portal-error">
            <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: C.amber }} />
            <p className="text-sm font-medium" style={{ color: C.navy }}>{error}</p>
            <p className="text-xs mt-2" style={{ color: C.slate }}>Please contact your broker for a fresh link.</p>
          </div>
        )}

        {p && (
          <>
            <div className="bg-white rounded-sm p-6" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="portal-policy-card">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <Overline>Your policy</Overline>
                  <h1 className="text-xl font-semibold" style={{ color: C.navy }}>{p.business_name}</h1>
                  <p className="text-sm mt-0.5" style={{ color: C.slate }}>{p.class} · <span className="font-mono">{p.policy_number}</span></p>
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold capitalize" style={{ background: `${statusColor}15`, color: statusColor }} data-testid="portal-policy-status">{p.status}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6">
                {[['Period of cover', `${fmtD(p.inception_date)} – ${fmtD(p.expiry_date)}`],
                  ['Annual premium', fmt$(p.annual_premium)],
                  ['Sum insured', fmt$(p.sum_insured)],
                  ['Your broker', p.brokerage_name || p.broker_name || '—']].map(([l, v]) => (
                  <div key={l}>
                    <p className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{l}</p>
                    <p className="text-sm font-medium mt-1" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{v}</p>
                  </div>
                ))}
              </div>
            </div>

            {data.renewal && (
              <RenewalOfferCard token={token} renewal={data.renewal}
                onAccepted={(resp) => setData(d => ({ ...d, renewal: { ...d.renewal, status: 'accepted', new_policy_number: resp.new_policy_number, new_expiry: resp.new_expiry || d.renewal.new_expiry } }))} />
            )}

            <div className="bg-white rounded-sm p-6" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="portal-documents">
              <Overline>Your documents</Overline>
              <div className="flex flex-wrap gap-3">
                <a href={`${API}/api/portal/policy/${token}/document/schedule`} className="flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }} data-testid="portal-doc-schedule">
                  <Download className="w-4 h-4" /> Policy Schedule
                </a>
                <a href={`${API}/api/portal/policy/${token}/document/certificate`} className="flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium" style={{ background: 'rgba(29,158,117,0.08)', color: C.green }} data-testid="portal-doc-certificate">
                  <Download className="w-4 h-4" /> Certificate of Currency
                </a>
              </div>
            </div>

            <div className="bg-white rounded-sm p-6" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="portal-claims">
              <div className="flex items-center justify-between">
                <Overline>Claims on this policy</Overline>
                {data.can_lodge_claim && !lodged && (
                  <button onClick={() => setShowFnol(s => !s)} className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: C.blue }} data-testid="portal-report-claim-btn">
                    Report a claim {showFnol ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
              {lodged && (
                <div className="flex items-center gap-3 p-4 rounded-sm mt-2" style={{ background: 'rgba(29,158,117,0.08)', border: '1px solid rgba(29,158,117,0.3)' }} data-testid="portal-fnol-success">
                  <ShieldCheck className="w-5 h-5 shrink-0" style={{ color: C.green }} />
                  <div className="text-sm">
                    <p className="font-semibold" style={{ color: C.green }}>Claim {lodged.claim_reference} received</p>
                    <p className="text-xs mt-0.5" style={{ color: C.slate }}>{lodged.message}</p>
                  </div>
                </div>
              )}
              {showFnol && !lodged && <FnolForm token={token} onLodged={(d) => { setLodged(d); setShowFnol(false); }} />}
              {data.claims.length === 0 && !lodged ? (
                <p className="text-sm mt-2" style={{ color: C.slate }}>No claims on record — nothing outstanding.</p>
              ) : (
                <div className="divide-y divide-[rgba(136,150,168,0.1)] mt-2">
                  {data.claims.map(cl => (
                    <div key={cl.claim_reference} className="flex items-center justify-between py-2.5 text-sm">
                      <div>
                        <span className="font-mono text-xs" style={{ color: C.navy }}>{cl.claim_reference}</span>
                        <span className="ml-2 capitalize" style={{ color: C.slate }}>{(cl.claim_type || '').replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs" style={{ color: C.slate }}>
                        <span>{fmtD(cl.date_of_loss)}</span>
                        <span className="capitalize px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(136,150,168,0.12)', color: C.navy }}>{(cl.status || '').replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        <p className="text-[10px] text-center pt-4" style={{ color: '#64748B' }}>Powered by underAIter.com — AI-Native Underwriting</p>
      </div>
    </div>
  );
}
